/* eslint-disable react-hooks/set-state-in-effect, @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

export default function PWARegister() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installInstructions, setInstallInstructions] = useState<"iOS" | "desktop" | "mobile-generic" | "generic" | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Check if the early inline script already stashed the event
      const earlyPrompt = (window as any).deferredPrompt;
      if (earlyPrompt) {
        setDeferredPrompt(earlyPrompt);
      }

      // 1. Check if already running in standalone (installed) mode
      const checkStandalone = 
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true;
      
      setIsStandalone(checkStandalone);

      let fallbackTimer: any = null;

      // Force-open the banner/dialog if "?install" is in the URL and not already installed
      const hasInstallParam = window.location.search.includes("install");
      if (hasInstallParam && !checkStandalone) {
        setShowInstallBanner(true);

        // If iOS, show custom instructions immediately since beforeinstallprompt won't fire
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
        if (isIOS) {
          setInstallInstructions("iOS");
        } else {
          // If not iOS, set a fallback timer to show generic instructions if deferredPrompt doesn't fire within 1.5s
          fallbackTimer = setTimeout(() => {
            setDeferredPrompt((prevPrompt: any) => {
              if (!prevPrompt) {
                const isMobile = /Mobi|Android/i.test(navigator.userAgent);
                setInstallInstructions(isMobile ? "mobile-generic" : "desktop");
              }
              return prevPrompt;
            });
          }, 1500);
        }
      }

      // 2. Listen for the browser's install prompt event
      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault();
        // Stash the event so it can be triggered later.
        setDeferredPrompt(e);
        (window as any).deferredPrompt = e;
        // Only show if NOT already installed and not dismissed
        const isDismissed = localStorage.getItem("pwa-install-dismissed") === "true";
        if (!checkStandalone && !isDismissed) {
          setShowInstallBanner(true);
        }
      };

      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

      // 3. Register service worker after page load to maintain pristine initial loading speed
      const registerSW = () => {
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.register("/sw.js").then(
            (registration) => {
              console.log("PWA Service Worker registered successfully: ", registration.scope);
            },
            (err) => {
              console.error("PWA Service Worker registration failed: ", err);
            }
          );
        }
      };

      if (document.readyState === "complete") {
        registerSW();
      } else {
        window.addEventListener("load", registerSW);
      }

      return () => {
        if (fallbackTimer) clearTimeout(fallbackTimer);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.removeEventListener("load", registerSW);
      };
    }
  }, []);

  // Attempt auto-prompt if "?install" is in URL and deferredPrompt becomes available
  useEffect(() => {
    if (deferredPrompt && typeof window !== "undefined") {
      const hasInstallParam = window.location.search.includes("install");
      if (hasInstallParam) {
        try {
          const promptResult = deferredPrompt.prompt();
          if (promptResult && typeof promptResult.catch === "function") {
            promptResult.catch((err: any) => {
              console.warn("Programmatic PWA prompt blocked/failed, keeping custom banner open:", err);
            });
          }
        } catch (err) {
          console.warn("Programmatic PWA prompt threw error, keeping custom banner open:", err);
        }
      }
    }
  }, [deferredPrompt]);

  // Listen for the custom "triggerPWAInstall" event to initiate install flow from menus
  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleTriggerPWAInstall = () => {
        const activePrompt = (window as any).deferredPrompt || deferredPrompt;
        if (activePrompt) {
          try {
            activePrompt.prompt();
            activePrompt.userChoice.then((choiceResult: { outcome: string }) => {
              if (choiceResult.outcome === "accepted") {
                setShowInstallBanner(false);
              }
              (window as any).deferredPrompt = null;
              setDeferredPrompt(null);
            });
          } catch (err) {
            console.error("Direct programmatic trigger failed, showing custom banner fallback:", err);
            setShowInstallBanner(true);
          }
        } else {
          setShowInstallBanner(true);
          setInstallInstructions(null);
        }
      };

      window.addEventListener("triggerPWAInstall", handleTriggerPWAInstall);
      return () => {
        window.removeEventListener("triggerPWAInstall", handleTriggerPWAInstall);
      };
    }
  }, []);

  // Synchronize state with global window object
  useEffect(() => {
    if (typeof window !== "undefined" && deferredPrompt) {
      (window as any).deferredPrompt = deferredPrompt;
    }
  }, [deferredPrompt]);

  // Listen for the custom "pwaInstalledSuccess" event to hide banner
  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleSuccess = () => {
        setShowInstallBanner(false);
      };
      window.addEventListener("pwaInstalledSuccess", handleSuccess);
      return () => {
        window.removeEventListener("pwaInstalledSuccess", handleSuccess);
      };
    }
  }, []);

  const handleInstallClick = () => {
    const activePrompt = (window as any).deferredPrompt || deferredPrompt;
    if (activePrompt) {
      try {
        // Show the native browser install prompt
        const promptResult = activePrompt.prompt();
        if (promptResult && typeof promptResult.catch === "function") {
          promptResult.catch((err: any) => {
            console.warn("PWA prompt rejection caught in banner click:", err);
          });
        }
        // Wait for the user to respond to the prompt
        activePrompt.userChoice.then((choiceResult: { outcome: string }) => {
          if (choiceResult.outcome === "accepted") {
            console.log("User accepted the install prompt");
            setShowInstallBanner(false);
          } else {
            console.log("User dismissed the install prompt");
            setShowInstallBanner(false);
            if (typeof window !== "undefined") {
              localStorage.setItem("pwa-install-dismissed", "true");
            }
          }
          setDeferredPrompt(null);
          (window as any).deferredPrompt = null;
        });
      } catch (err) {
        console.error("Failed to run activePrompt.prompt() from banner:", err);
      }
    } else {
      // Native prompt not ready/supported (e.g. iOS Safari) - show interactive instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.userAgent.includes("Mac") && "ontouchend" in document);
      
      if (isIOS) {
        setInstallInstructions("iOS");
      } else {
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);
        setInstallInstructions(isMobile ? "mobile-generic" : "desktop");
      }
    }
  };

  const handleDismiss = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem("pwa-install-dismissed", "true");
    }
    setShowInstallBanner(false);
  };

  if (!showInstallBanner || isStandalone) return null;

  return (
    <>
      <style>{`
        .pwa-install-banner-container {
          position: fixed !important;
          bottom: 24px !important;
          left: 24px !important;
          right: 24px !important;
          z-index: 2147483647 !important;
          animation: pwa-slide-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @media (min-width: 768px) {
          .pwa-install-banner-container {
            left: auto !important;
            right: 32px !important;
            width: 384px !important;
          }
        }
        .pwa-install-banner-card {
          background: #0f172a !important;
          background: rgba(15, 23, 42, 0.96) !important;
          backdrop-filter: blur(20px) !important;
          -webkit-backdrop-filter: blur(20px) !important;
          border: 1px solid rgba(255, 255, 255, 0.12) !important;
          padding: 24px !important;
          border-radius: 24px !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4) !important;
          text-align: left !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 16px !important;
          color: #ffffff !important;
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }
        .pwa-icon-container {
          width: 48px !important;
          height: 48px !important;
          border-radius: 12px !important;
          background-color: rgba(78, 222, 163, 0.15) !important;
          border: 1px solid rgba(78, 222, 163, 0.3) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex-shrink: 0 !important;
          animation: pwa-pulse 2s infinite !important;
        }
        .pwa-title {
          font-size: 15px !important;
          font-weight: 800 !important;
          color: #ffffff !important;
          margin: 0 !important;
          letter-spacing: -0.02em !important;
          line-height: 1.25 !important;
        }
        .pwa-description {
          font-size: 12px !important;
          font-weight: 500 !important;
          color: #94a3b8 !important;
          line-height: 1.5 !important;
          margin: 4px 0 0 0 !important;
        }
        .pwa-instructions-panel {
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #e2e8f0 !important;
          line-height: 1.5 !important;
          background-color: rgba(30, 41, 59, 0.6) !important;
          padding: 12px !important;
          border-radius: 12px !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 6px !important;
          margin: 4px 0 0 0 !important;
        }
        .pwa-instructions-tag {
          font-size: 9px !important;
          font-weight: 900 !important;
          text-transform: uppercase !important;
          color: #34d399 !important;
          letter-spacing: 0.05em !important;
          margin: 0 0 2px 0 !important;
        }
        .pwa-buttons-container {
          display: flex !important;
          gap: 12px !important;
          margin-top: 4px !important;
        }
        .pwa-btn-primary {
          flex: 1 !important;
          padding: 10px 16px !important;
          border-radius: 12px !important;
          font-weight: 800 !important;
          font-size: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.08em !important;
          color: #ffffff !important;
          background-color: #006c49 !important;
          border: none !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          text-align: center !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2) !important;
        }
        .pwa-btn-primary:hover {
          transform: translateY(-1px) !important;
          background-color: #00875c !important;
          box-shadow: 0 6px 8px -1px rgba(0, 0, 0, 0.3) !important;
        }
        .pwa-btn-primary:active {
          transform: translateY(1px) !important;
        }
        .pwa-btn-secondary {
          padding: 10px 16px !important;
          border-radius: 12px !important;
          font-weight: 800 !important;
          font-size: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.08em !important;
          color: #94a3b8 !important;
          background-color: rgba(255, 255, 255, 0.06) !important;
          border: none !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          text-align: center !important;
        }
        .pwa-btn-secondary:hover {
          background-color: rgba(255, 255, 255, 0.12) !important;
          color: #ffffff !important;
        }
        .pwa-badge {
          display: inline-flex !important;
          align-items: center !important;
          padding: 2px 6px !important;
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-radius: 4px !important;
          gap: 4px !important;
          font-size: 10px !important;
          vertical-align: middle !important;
        }
        @keyframes pwa-slide-in {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes pwa-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.75; }
        }
      `}</style>
      <div className="pwa-install-banner-container">
        <div className="pwa-install-banner-card">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div className="pwa-icon-container">
              <span className="material-symbols-outlined text-2xl font-bold" style={{ color: "#4edea3" }}>install_mobile</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 className="pwa-title">Install HITCLICKS Academy</h4>
              {installInstructions === null ? (
                <p className="pwa-description">
                  📲 Add HITCLICKS Academy to your Home Screen for faster access to your classes, AI tools, and business growth—anytime you need them. 🚀
                </p>
              ) : installInstructions === "iOS" ? (
                <div className="pwa-instructions-panel">
                  <p className="pwa-instructions-tag">iOS Safari Instructions:</p>
                  <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    1. Tap the <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>share</span> Share</span> button in Safari.
                  </p>
                  <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    2. Select <span style={{ fontWeight: 900, color: '#ffffff' }}>&quot;Add to Home Screen&quot;</span> <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>add_box</span></span>.
                  </p>
                </div>
              ) : installInstructions === "desktop" ? (
                <div className="pwa-instructions-panel" style={{ gap: '10px' }}>
                  <p className="pwa-instructions-tag">Windows/Desktop Instructions:</p>
                  
                  <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '6px', marginBottom: '2px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: 800, color: '#34d399', fontSize: '10px' }}>Chrome:</p>
                    <p style={{ margin: 0, paddingLeft: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                      1. Click the <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>install_desktop</span> Install</span> icon in the top-right address bar.
                    </p>
                    <p style={{ margin: '4px 0 0 0', paddingLeft: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                      2. Or open the menu <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>more_vert</span></span> &rarr; select <span style={{ color: '#ffffff', fontWeight: 800 }}>&quot;Save and share&quot;</span> &rarr; <span style={{ color: '#ffffff', fontWeight: 800 }}>&quot;Install page as app&quot;</span>.
                    </p>
                  </div>

                  <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '6px', marginBottom: '2px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: 800, color: '#38bdf8', fontSize: '10px' }}>Microsoft Edge:</p>
                    <p style={{ margin: 0, paddingLeft: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                      1. Click the <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>apps</span> App available</span> icon in the address bar.
                    </p>
                    <p style={{ margin: '4px 0 0 0', paddingLeft: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                      2. Or open the menu <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>more_horiz</span></span> &rarr; select <span style={{ color: '#ffffff', fontWeight: 800 }}>&quot;Apps&quot;</span> &rarr; <span style={{ color: '#ffffff', fontWeight: 800 }}>&quot;Install this site as an app&quot;</span>.
                    </p>
                  </div>

                  <div>
                    <p style={{ margin: '0 0 2px 0', fontWeight: 800, color: '#ff7849', fontSize: '10px' }}>Firefox:</p>
                    <p style={{ margin: 0, paddingLeft: '8px', fontSize: '10px', color: '#94a3b8' }}>
                      Firefox on desktop does not natively support PWA installation. Please use <span style={{ color: '#ffffff', fontWeight: 700 }}>Chrome</span> or <span style={{ color: '#ffffff', fontWeight: 700 }}>Microsoft Edge</span> for the best experience!
                    </p>
                  </div>
                </div>
              ) : (
                <div className="pwa-instructions-panel">
                  <p className="pwa-instructions-tag">Android/Mobile Instructions:</p>
                  <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    1. Tap the browser menu <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>more_vert</span></span> or <span className="pwa-badge"><span className="material-symbols-outlined" style={{ fontSize: '12px' }}>download</span></span> icon.
                  </p>
                  <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    2. Select <span style={{ fontWeight: 900, color: '#ffffff' }}>&quot;Install App&quot;</span> or <span style={{ fontWeight: 900, color: '#ffffff' }}>&quot;Add to Home Screen&quot;</span>.
                  </p>
                </div>
              )}
            </div>
          </div>

          {installInstructions === null ? (
            <div className="pwa-buttons-container">
              <button 
                onClick={handleInstallClick}
                className="pwa-btn-primary"
              >
                Install App
              </button>
              <button 
                onClick={handleDismiss}
                className="pwa-btn-secondary"
              >
                Dismiss
              </button>
            </div>
          ) : (
            <div className="pwa-buttons-container">
              <button 
                onClick={() => {
                  handleDismiss();
                  setInstallInstructions(null);
                }}
                className="pwa-btn-primary"
              >
                Got it, Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
